import React from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, Grid, InputAdornment, Typography } from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { fireFightingPumpTypeEnum, firePumpBuildTypeEnum } from "common/enums";

import { onlyNumberKeys } from "utils";

const FireFightingPumpDetails = ({ formik, t }) => {
  const {
    values: {
      fireProtection: { fireFightingPumps },
    },
    setFieldValue,
    setFieldTouched,
  } = formik;

  const handleAddNewFightingPumpDetail = (
    existingfireFightingPumpDetailList
  ) => {
    const newFireFightingPumpDetails = [
      ...existingfireFightingPumpDetailList,
      {
        id: 0,
        type: "",
        numberOfPump: "",
        head: "",
        discharge: "",
        autoStartPressure: "",
        autoStopPressure: "",
        kwOrHp: "",
        pumpBuildType: "",
      },
    ];
    setFieldValue(
      "fireProtection.fireFightingPumps",
      newFireFightingPumpDetails
    );
    setFieldTouched(
      `fireProtection.fireFightingPumps[${
        newFireFightingPumpDetails.length - 1
      }]`,
      false,
      false
    );
  };

  const handleRemoveFightingPumpDetail = (index) => {
    setFieldValue(
      "fireProtection.fireFightingPumps",
      fireFightingPumps.filter((_, ind) => ind !== index)
    );
  };

  return (
    <>
      <Grid item className="border ps-3 pe-2 pb-2 pt-0" sx={{ mb: 3 }} xs={12}>
        <Grid item xs={12} className="mt-3">
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Box>
              <Typography className="mb-3 me-3" lineHeight={1} variant="h6">
                {`${t("Provide capacity of firefighting pumps")} (${t(
                  "If available"
                )})`}
              </Typography>
            </Box>
            <Box>
              <Button
                color="primary"
                className="mb-3"
                sx={{ minWidth: "unset", textTransform: "capitalize" }}
                variant="outlined"
                onClick={() =>
                  handleAddNewFightingPumpDetail(fireFightingPumps)
                }
              >
                <strong>
                  <span>+</span> {t("Add Fire Fighting Pump Details")}
                </strong>
              </Button>
            </Box>
          </Box>
        </Grid>
        {!!fireFightingPumps.length &&
          fireFightingPumps.map(
            (
              {
                discharge,
                head,
                numberOfPump,
                autoStartPressure,
                autoStopPressure,
                kwOrHp,
              },
              index
            ) => (
              <Grid
                key={index}
                container
                columnSpacing={1}
                rowSpacing={1}
                className="border p-2 pe-3 mt-2"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Pump Build Type") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.pumpBuildType`}
                    formik={formik}
                    options={firePumpBuildTypeEnum}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Fire Fighting Pump Type") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.type`}
                    formik={formik}
                    options={fireFightingPumpTypeEnum}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Number of Pump") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.numberOfPump`}
                    formik={formik}
                    type="number"
                    value={numberOfPump ?? ""}
                    placeholder="Enter Number Of Pump"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Head") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.head`}
                    formik={formik}
                    type="number"
                    value={head ?? ""}
                    placeholder="Enter Head"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">Mtrs</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Discharge") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.discharge`}
                    formik={formik}
                    type="number"
                    value={discharge ?? ""}
                    placeholder="Enter Discharge"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">LPM</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Auto Start Pressure") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.autoStartPressure`}
                    formik={formik}
                    type="number"
                    value={autoStartPressure ?? ""}
                    placeholder="Enter Auto Start Pressure"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          Kg/Cm<sup>2</sup>
                        </InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Auto Stop Pressure") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.autoStopPressure`}
                    formik={formik}
                    value={autoStopPressure ?? ""}
                    placeholder="Enter Auto Stop Pressure"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          Kg/Cm<sup>2</sup>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("KW / HP") + "*"}
                    name={`fireProtection.fireFightingPumps.${index}.kwOrHp`}
                    formik={formik}
                    type="number"
                    value={kwOrHp ?? ""}
                    placeholder="Enter KW / HP"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Button
                    color="error"
                    size="small"
                    sx={{
                      minWidth: "unset",
                      border: "1px solid",
                      height: "40px",
                      width: "100px",
                      mb: 2,
                    }}
                    onClick={() => handleRemoveFightingPumpDetail(index)}
                  >
                    <strong className="d-flex justify-content-center">
                      <DeleteOutlineSharpIcon fontSize="small" />
                      {t("Remove")}
                    </strong>
                  </Button>
                </Grid>
              </Grid>
            )
          )}
      </Grid>
    </>
  );
};

FireFightingPumpDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FireFightingPumpDetails);
